import { useEffect, useRef, useState } from 'react';

/**
 * Animates a numeric value counting up once it scrolls into view.
 * Accepts the display string (e.g. "11,400+", "48 hrs", "4.8 / 5"),
 * extracts the first number in it, and animates that number while
 * keeping the surrounding text intact.
 */
export default function CountUp({ value, start }) {
  const match = value.match(/[\d,.]+/);
  const [ran, setRan] = useState(false);
  const [display, setDisplay] = useState(match ? value.replace(match[0], zero(match[0])) : value);
  const frame = useRef(null);

  useEffect(() => {
    if (!start || ran || !match) return undefined;
    setRan(true);

    const raw = match[0];
    const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
    const target = parseFloat(raw.replace(/,/g, ''));
    const duration = 900;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = target * eased;
      const formatted = formatLike(raw, current, decimals);
      setDisplay(value.replace(raw, formatted));
      if (progress < 1) {
        frame.current = requestAnimationFrame(tick);
      }
    };

    frame.current = requestAnimationFrame(tick);
    return () => frame.current && cancelAnimationFrame(frame.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  return <>{display}</>;
}

function zero(raw) {
  if (raw.includes('.')) {
    const [, dec] = raw.split('.');
    return `0.${'0'.repeat(dec.length)}`;
  }
  return '0';
}

function formatLike(raw, num, decimals) {
  const fixed = num.toFixed(decimals);
  if (raw.includes(',')) {
    const [int, dec] = fixed.split('.');
    const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return dec ? `${withCommas}.${dec}` : withCommas;
  }
  return fixed;
}
